import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { contractGeneratorOperations, contractGeneratorSelectors } from 'Redux/ducks/contractGenerator';
import { FlexBox } from 'ViatoUi/Shared/Layouts/Flex';
import Spacer from 'ViatoUi/Shared/Layouts/Spacer';
import Paper from 'ViatoUi/Shared/Surfaces/Paper';
import { Headline } from 'ViatoUi/Shared/Typo';

import Field from './Field';
import styles from './Form.module.scss';
import validate from './utilities/validate';

const Form = ({ fieldInfo, formData, formFields, headline, showHeadline, children, formName, styling }) => {
	const dispatch = useDispatch();
	const { t } = useTranslation(['contractGenerator', 'translation']);

	const dataToSubmit = useSelector(contractGeneratorSelectors.getDataToSubmit);

	const fieldsToRender = useMemo(() => {
		const returnArray = [];

		formFields.forEach(fieldSet => {
			const setReturnArray = [];

			fieldSet.forEach(field => {
				let compareValue = get(dataToSubmit, field.conditions.field, '');
				if (typeof compareValue === 'object') {
					compareValue = compareValue.key;
				}

				if (
					!field.conditions.isConditional ||
					(field.conditions.isConditional && compareValue === field.conditions.needsToBeValue)
				) {
					setReturnArray.push({ ...field });
				}
			});

			returnArray.push(setReturnArray);
		});

		return returnArray;
	}, [dataToSubmit, formFields]);

	return (
		<Paper className={styles.form} elevation={styling.elevation} roundness={styling.roundness} spacing={styling.spacing}>
			<div id={`viato-contract-generator-form-${formName}`} />
			{showHeadline && <Headline type='h2'>{headline}</Headline>}
			<Spacer height='l' />
			<FlexBox className={styles.fieldColumnContainer}>
				{fieldsToRender.map(fieldSet => (
					<div key={fieldSet[0]?.name || 'none'} className={`fieldColumn ${styles.fieldColumn}`}>
						{fieldSet.map((field, fieldIndex) => (
							<Fragment key={field.name}>
								<Field
									errorMessages={get(fieldInfo, `${field.name}.errorMessages`, [])}
									inputSettings={field.inputSettings || {}}
									options={
										field.options
											? [
													...field.options.map(option => ({
														value: t(`translation:forms.${formName}.options.${field.name}.${option}`),
														key: option,
														label: t(`translation:forms.${formName}.options.${field.name}.${option}`)
													}))
											  ]
											: []
									}
									placeholder={t(`translation:forms.${formName}.${field.name}`)}
									required={field.required || false}
									showErrorMessage={
										!get(fieldInfo, `${field.name}.isValid`, true) &&
										get(fieldInfo, `${field.name}.showErrorMessage`, false)
									}
									tooltip={field.tooltip ? t(`translation:forms.${formName}.tooltips.${field.name}`) : null}
									type={field.type}
									value={get(formData, field.name, '')}
									onChange={newValue => {
										const preparedNewValue = newValue;
										let userValue = newValue;
										if (typeof newValue === 'object') {
											userValue = newValue.value;
											if (newValue.label) {
												delete preparedNewValue.label;
											}
										}

										dispatch(
											contractGeneratorOperations.setDataToSubmit({
												formData: { [formName]: { [field.name]: preparedNewValue } }
											})
										);
										dispatch(
											contractGeneratorOperations.setFieldInfo({
												formData: {
													[formName]: {
														[field.name]: validate(
															userValue,
															field.name,
															`${headline}: ${t(`translation:forms.${formName}.${field.name}`)}`,
															t,
															field.required,
															formName
														)
													}
												}
											})
										);
									}}
									onMount={() =>
										dispatch(
											contractGeneratorOperations.setFieldInfo({
												formData: {
													[formName]: {
														[field.name]: validate(
															get(formData, field.name, ''),
															field.name,
															`${headline}: ${t(`translation:forms.${formName}.${field.name}`)}`,
															t,
															field.required,
															formName,
															false
														)
													}
												}
											})
										)
									}
									onUnmount={() => {
										dispatch(
											contractGeneratorOperations.deleteKey(
												`userInput.dataToSubmit.formData.${formName}.${field.name}`
											)
										);
										dispatch(
											contractGeneratorOperations.deleteKey(`userInput.fieldInfo.formData.${formName}.${field.name}`)
										);
									}}
								/>
								{fieldIndex + 1 < fieldSet.length && <Spacer height={styling.spacerHeight} />}
							</Fragment>
						))}
					</div>
				))}
			</FlexBox>
			{children}
		</Paper>
	);
};

Form.propTypes = {
	children: PropTypes.node,
	headline: PropTypes.string.isRequired,
	showHeadline: PropTypes.bool,
	formFields: PropTypes.array.isRequired,
	fieldInfo: PropTypes.object.isRequired,
	formName: PropTypes.string.isRequired,
	formData: PropTypes.object.isRequired,
	styling: PropTypes.object
};

Form.defaultProps = {
	showHeadline: true,
	children: null,
	styling: {
		elevation: 'strong',
		roundness: 'strong',
		spacing: 'xxl',
		spacerHeight: 'xl'
	}
};

export default Form;
